/**
 * src/components/approvals/RejectReasonModal.tsx
 * Modal prompting Admin for an optional rejection reason note
 */

import React, { useState } from 'react';
import { XCircle, AlertCircle } from 'lucide-react';
import { GlassModal } from '../ui/GlassModal';
import { GlassButton } from '../ui/GlassButton';

interface RejectReasonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
  requestTitle?: string;
}

export const RejectReasonModal: React.FC<RejectReasonModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  requestTitle
}) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onConfirm(reason.trim());
      setReason('');
      onClose();
    } catch {
      // error handled by parent toast
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <GlassModal
      isOpen={isOpen}
      onClose={onClose}
      title="Reject Transaction Request"
      maxWidth="440px"
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        <div style={{
          display: 'flex',
          gap: '0.75rem',
          padding: '0.875rem',
          borderRadius: 'var(--radius-md)',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.25)',
          color: '#f87171',
          fontSize: '0.875rem'
        }}>
          <AlertCircle size={20} style={{ flexShrink: 0 }} />
          <div>
            Are you sure you want to reject this request? The user will be notified and no balance changes will be made.
            {requestTitle && <div style={{ marginTop: '0.35rem', fontWeight: 600 }}>{requestTitle}</div>}
          </div>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
            REASON FOR REJECTION (OPTIONAL)
          </label>
          <textarea
            rows={3}
            placeholder="e.g. Duplicate entry, incorrect amount, please clarify items..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              borderRadius: 'var(--radius-md)',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid var(--border-glass)',
              color: 'var(--text-primary)',
              fontSize: '0.875rem',
              outline: 'none',
              resize: 'vertical'
            }}
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
          <GlassButton type="button" variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </GlassButton>
          <GlassButton type="submit" variant="crimson" disabled={submitting}>
            <XCircle size={16} />
            {submitting ? 'Rejecting...' : 'Confirm Rejection'}
          </GlassButton>
        </div>
      </form>
    </GlassModal>
  );
};
